import * as React from "react";
import classNames from "clsx";
import { Slot } from "@radix-ui/react-slot";
import { textPropDefs } from "./text.props";
import {
  extractMarginProps,
  withMarginProps,
  withBreakpoints,
} from "../../../helpers";

import type {
  MarginProps,
  GetPropDefTypes,
  NiceIntersection,
} from "../../../helpers";

type TextElement = React.ElementRef<"span">;
type TextOwnProps = GetPropDefTypes<typeof textPropDefs>;
type CommonTextProps = NiceIntersection<MarginProps, TextOwnProps>;

type TextAsChildProps = {
  asChild?: boolean;
  as?: never;
} & React.ComponentPropsWithRef<"span">;

type TextSpanProps = {
  as?: "span";
  asChild?: never;
} & React.ComponentPropsWithRef<"span">;

type TextDivProps = {
  as: "div";
  asChild?: never;
} & React.ComponentPropsWithRef<"div">;

type TextLabelProps = {
  as: "label";
  asChild?: never;
} & React.ComponentPropsWithRef<"label">;

type TextPProps = {
  as: "p";
  asChild?: never;
} & React.ComponentPropsWithRef<"p">;

type TextProps = CommonTextProps &
  (
    | TextAsChildProps
    | TextSpanProps
    | TextDivProps
    | TextLabelProps
    | TextPProps
  );

const Text = React.forwardRef<TextElement, TextProps>((props, forwardedRef) => {
  const { rest: marginRest, ...marginProps } = extractMarginProps(props);
  const {
    children,
    className,
    asChild = false,
    as: Tag = "span",
    weight = textPropDefs.weight.default,
    align = textPropDefs.align.default,
    trim = textPropDefs.trim.default,
    variant = textPropDefs.variant.default,
    ...textProps
  } = marginRest;
  return (
    <Slot
      {...textProps}
      ref={forwardedRef}
      className={classNames(
        "in-text",
        `in-text--${variant}`,
        className,
        withBreakpoints(weight, "in-r-weight"),
        withBreakpoints(align, "in-r-ta"),
        withBreakpoints(trim, "in-r-lt"),
        withMarginProps(marginProps)
      )}
    >
      {asChild ? children : <Tag>{children}</Tag>}
    </Slot>
  );
});
Text.displayName = "Text";

export { Text };
export type { TextProps };
